/**
 * Customer detail — one person's record: who they are, what the SIS knows, and
 * everything they have done, in their own time zone.
 *
 * Route `customers.detail`, URL `/customers/<customerId>`. It survives a direct
 * navigation, a refresh and browser back/forward because the id is in the path,
 * not in a component's memory.
 *
 * Read-only except the customer-type assignment. Sections, in order: identity,
 * SIS-synchronised profile, appointments, events and registrations, walk-in
 * queue visits, messaging consent.
 *
 * The three history sections are one read, split by `department`. They are
 * hidden — not emptied — without `customers.service_record.read`, and a 403 on
 * the read hides them the same way: an operator who may not see a history
 * should not be told how empty it is.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Btn, Icon, PageState, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { useIsMobile } from "../../shell/useResponsive";
import { useT } from "../../lib/i18n";
import { friendlyDateTime, friendlyTimeRange } from "../../lib/datetime";
import { useCustomerTypeCatalog } from "./customer-types";
import { CustomerTypeCard } from "./CustomerTypeCard";
import CustomerMessagingConsent from "./CustomerMessagingConsent";
import {
  CUSTOMER_OPS, SERVICE_RECORD_LIMIT_MAX, continuationBound, getCustomer, getCustomerServiceRecord, scopeProbe,
  useScopedRead, type ServiceRecordDepartment, type ServiceRecordEntry,
} from "./customers-data";
import { EM_DASH, humaniseFieldKey, projectSisProfile } from "./customer-columns";
import { DetailSection, Facts, Kpi, LifecycleChip, ReadError, useCustomerNoun, useIcu, initialsOf } from "./customers-shared";

export default function CustomerDetail() {
  const t = useT();
  const icu = useIcu();
  const nav = useNav();
  const { noun, nounPlural } = useCustomerNoun();
  const customerId = typeof nav.params.customerId === "string" ? nav.params.customerId : "";
  // Two columns once there is room for them: identity + synchronised profile on
  // the left, the activity history and consent on the right. Below that the
  // same sections stack in the drop's order.
  const twoUp = !useIsMobile(1100);
  const catalog = useCustomerTypeCatalog(can("customer_types.read"));

  const identityRead = useScopedRead(`customer|${customerId}`, () => getCustomer(customerId), !!customerId);
  const identity = identityRead.data?.data ?? null;
  const redirectTo = identityRead.data?.redirectTo ?? null;

  /** Point reread for the type card: by id, never a directory scan. */
  const rereadType = useCallback(async (): Promise<string | null | undefined> => {
    const inScope = scopeProbe();
    try {
      const res = await getCustomer(customerId);
      if (!inScope()) return undefined;
      identityRead.reload();
      return res.data.customerTypeId ?? undefined;
    } catch { return undefined; }
  }, [customerId, identityRead]);

  const back = (
    <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("customers")}>
      <Icon.arrow dir="left" size={11} /> {icu("customers.detail.back", "All {nounPlural}", { nounPlural })}
    </span>
  );

  // No id in the path (`/customers/detail` typed by hand) is a not-found, not a
  // blank screen waiting for a read that will never be issued.
  if (!customerId || identityRead.failure?.notFound) {
    return (
      <Screen rail="customers">
        <PageHeader title={icu("customers.detail.notFound.title", "{noun} not found", { noun })} breadcrumb={back} />
        <div style={{ padding: "14px 24px 48px" }}>
          <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, padding: 24, display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}>
            <p style={{ margin: 0, fontSize: 13.5, color: T.n2 }}>{t("customers.detail.notFound.body", "This record may have been merged or removed.")}</p>
            <Btn variant="secondary" size="sm" onClick={() => nav.go("customers")}>
              {icu("customers.detail.notFound.back", "Back to {nounPlural}", { nounPlural })}
            </Btn>
          </div>
        </div>
      </Screen>
    );
  }

  const displayName = identity?.name?.displayName
    || [identity?.name?.firstName, identity?.name?.lastName].filter(Boolean).join(" ")
    || (identity ? identity.customerId : "");

  const externalIds = identity?.externalIds;
  const sis = projectSisProfile(identity?.sisAttributes ?? null);
  const historyAllowed = can("customers.service_record.read");

  return (
    <Screen rail="customers">
      <PageHeader title={displayName || icu("customers.detail.loading", "Loading {noun}\u2026", { noun: noun.toLowerCase() })} breadcrumb={back} />
      <DataSourceStrip screen="customers" ops={CUSTOMER_OPS} reqId="REQ-069" />

      <div style={{ padding: "14px 24px 48px", display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
        <PageState
          noun={noun.toLowerCase()}
          loading={identityRead.loading}
          error={identityRead.failure && !identityRead.failure.notFound
            ? (identityRead.failure.denied
              ? t("customers.detail.denied", "Your role can\u2019t open this record.")
              : identityRead.failure.message)
            : null}
          onRetry={identityRead.failure?.denied ? undefined : identityRead.reload}
          retryLabel={t("common.actions.retry", "Retry")}
          skeleton="table"
          skeletonRows={4}
        >
          {identity ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {identity.status === "merged" ? (
                <div role="status" style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 14px", borderRadius: 8, background: T.orange2, fontSize: 12.5, color: T.n1, flexWrap: "wrap" }}>
                  <span style={{ display: "inline-flex", color: T.orange1 }}><Icon.warn size={14} /></span>
                  <span style={{ flex: 1, minWidth: 200 }}>
                    {icu("customers.detail.identity.mergedInto", "Merged into {target}", { target: identity.mergedIntoCustomerId ?? EM_DASH })}
                  </span>
                  {redirectTo ? (
                    <Btn variant="secondary" size="sm" onClick={() => nav.go("customers.detail", { customerId: redirectTo.split("/").pop() ?? redirectTo })}>
                      {t("customers.detail.identity.openMerged", "Open current record")}
                    </Btn>
                  ) : null}
                </div>
              ) : null}

              {/* Identity and the synchronised profile sit side by side: two
                  read-only descriptions of the same person, equally weighted.
                  Activity and consent run full width beneath them. */}
              <div style={{ display: "grid", gridTemplateColumns: twoUp ? "minmax(0, 1fr) minmax(0, 1fr)" : "minmax(0, 1fr)", gap: 16, alignItems: "start", minWidth: 0 }}>
              {/* 1 — identity */}
              <DetailSection title={t("customers.detail.identity.title", "Identity")}>
                <div style={{ display: "flex", gap: 12, alignItems: "center", padding: "14px 16px", borderBottom: `1px solid ${T.n6}` }}>
                  <span style={{
                    width: 44, height: 44, borderRadius: 999, background: T.p1, color: T.onColor,
                    display: "grid", placeItems: "center", fontSize: 15, fontWeight: 600, flexShrink: 0,
                  }}>{initialsOf(displayName)}</span>
                  <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>{displayName}</span>
                    <span style={{ fontFamily: T.fontMono, fontSize: 11, color: T.soft }}>{identity.customerId}</span>
                  </span>
                </div>
                <Facts rows={[
                  {
                    label: t("customers.detail.identity.status", "Status"),
                    value: t(`customers.detail.status.${identity.status}`, humaniseFieldKey(identity.status)),
                  },
                  {
                    label: t("customers.detail.identity.externalIds", "External IDs"),
                    value: externalIds === undefined
                      ? <span style={{ color: T.soft }}>{t("customers.detail.identity.externalIdsPending", "Not available on this record yet.")}</span>
                      : externalIds.length
                        ? externalIds.map((x) => `${x.system.toUpperCase()} ${x.value}`).join(" \u00b7 ")
                        : null,
                  },
                  {
                    label: t("customers.detail.identity.emails", "Email addresses"),
                    value: identity.emails?.length
                      ? <ContactList items={identity.emails.map((e) => ({ value: e.value, meta: [e.purpose, e.verification].filter(Boolean).join(" \u00b7 ") }))} />
                      : <span style={{ color: T.soft }}>{t("customers.detail.identity.noEmails", "No email address on file.")}</span>,
                  },
                  {
                    label: t("customers.detail.identity.phones", "Phone numbers"),
                    value: identity.phones?.length
                      ? <ContactList items={identity.phones.map((p) => ({ value: p.value, meta: [p.purpose, p.verification].filter(Boolean).join(" \u00b7 ") }))} />
                      : <span style={{ color: T.soft }}>{t("customers.detail.identity.noPhones", "No phone number on file.")}</span>,
                  },
                  {
                    label: t("customers.detail.identity.address", "Address"),
                    value: identity.postalAddresses?.length
                      ? <ContactList items={identity.postalAddresses.map((a) => ({
                        value: [a.addressLine1, a.addressLine2, a.city, a.postcode, a.country].filter(Boolean).join(", "),
                        meta: a.purpose ?? "",
                      }))} />
                      : <span style={{ color: T.soft }}>{t("customers.detail.identity.noAddress", "No address on file.")}</span>,
                  },
                  {
                    label: t("customers.detail.identity.localeTimezone", "Language & time zone"),
                    value: [identity.locale, identity.timezone].filter(Boolean).join(" \u00b7 ") || null,
                  },
                ]} />
                <div style={{ borderTop: `1px solid ${T.n6}` }}>
                  {identity.customerTypeId === undefined ? (
                    <p style={{ margin: 0, padding: "14px 16px", fontSize: 12.5, color: T.soft }}>
                      {t("customers.type.pending", "The type assignment isn\u2019t available on this record yet, so it can\u2019t be shown or changed here.")}
                    </p>
                  ) : (
                    <CustomerTypeCard
                      customerId={identity.customerId}
                      currentTypeId={identity.customerTypeId ?? null}
                      catalog={catalog}
                      onRefetch={rereadType}
                    />
                  )}
                </div>
              </DetailSection>

              {/* 2 — SIS-synchronised profile */}
              <DetailSection title={t("customers.detail.sis.title", "Synchronised profile")}>
                {identity.sisAttributes === undefined ? (
                  <p style={{ margin: 0, padding: "14px 16px", fontSize: 12.5, color: T.soft }}>
                    {t("customers.detail.sis.pending", "Synchronised profile data isn\u2019t available on this record yet.")}
                  </p>
                ) : identity.sisAttributes === null || (!sis.program && !sis.holds.length && !sis.scalars.length) ? (
                  <p style={{ margin: 0, padding: "14px 16px", fontSize: 12.5, color: T.soft }}>
                    {t("customers.detail.sis.empty", "Nothing is synchronised for this record.")}
                  </p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {sis.program ? (
                      <Facts rows={[
                        { label: t("customers.detail.sis.program", "Program"), value: [sis.program.title, sis.program.code].filter(Boolean).join(" \u00b7 ") || null },
                        { label: t("customers.detail.sis.level", "Level"), value: sis.program.level ?? null },
                        { label: t("customers.detail.sis.programStatus", "Program status"), value: sis.program.status ?? null },
                        { label: t("customers.detail.sis.campus", "Campus"), value: sis.program.campus ?? null },
                        { label: t("customers.detail.sis.asOf", "As of"), value: sis.program.asOf ? friendlyDateTime(sis.program.asOf) : null },
                      ]} />
                    ) : null}
                    {sis.holds.length ? (
                      <div style={{ padding: "0 16px 14px" }}>
                        <span style={{ fontSize: 11.5, fontWeight: 700, color: T.n1, display: "block", marginBottom: 6 }}>{t("customers.detail.sis.holds", "Holds")}</span>
                        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                          {sis.holds.map((h, i) => (
                            <li key={`${h.code ?? "hold"}-${i}`} style={{ padding: "8px 10px", borderRadius: 6, background: T.orange2, fontSize: 12.5, color: T.n1 }}>
                              <span style={{ fontWeight: 600 }}>{h.code ?? t("customers.detail.sis.hold", "Hold")}</span>
                              {h.reason ? ` \u2014 ${h.reason}` : ""}
                              <span style={{ display: "block", fontSize: 11.5, color: T.n2 }}>
                                {[h.office, h.placedAt ? friendlyDateTime(h.placedAt) : null, h.blocks?.length ? h.blocks.join(", ") : null].filter(Boolean).join(" \u00b7 ")}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {sis.scalars.length ? (
                      <Facts rows={sis.scalars.map((s) => ({
                        label: t(`customers.columns.sis:${s.key.toLowerCase()}`, humaniseFieldKey(s.key)),
                        value: typeof s.value === "boolean" ? (s.value ? t("common.yes", "Yes") : t("common.no", "No")) : String(s.value),
                      }))} />
                    ) : null}
                    {sis.unprojected.length ? (
                      <p style={{ margin: 0, padding: "0 16px 14px", fontSize: 11.5, color: T.soft }}>
                        {icu("customers.detail.sis.unprojected", "Not shown here yet: {fields}.", { fields: sis.unprojected.map(humaniseFieldKey).join(", ") })}
                      </p>
                    ) : null}
                  </div>
                )}
              </DetailSection>

              </div>

              {/* 3–5 — history */}
              {historyAllowed ? <History customerId={identity.customerId} /> : null}

              {/* 6 — consent */}
              <CustomerMessagingConsent customerId={identity.customerId} />
            </div>
          ) : null}
        </PageState>
      </div>
    </Screen>
  );
}

function ContactList({ items }: { items: Array<{ value: string; meta: string }> }) {
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
      {items.map((x, i) => (
        <li key={`${x.value}-${i}`} style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: T.n1, overflowWrap: "anywhere" }}>{x.value}</span>
          {x.meta ? <span style={{ fontSize: 11, color: T.soft }}>{x.meta}</span> : null}
        </li>
      ))}
    </ul>
  );
}

const DEPARTMENTS: ServiceRecordDepartment[] = ["appointments", "events", "queues"];

/**
 * The three history sections. One read per page of history; continuation moves
 * the exclusive `to` bound to the oldest occurrence in hand.
 *
 * ⚠ That bound can skip entries sharing a page-boundary timestamp (CC dep 6).
 * Until lossless continuation ships, truncation is stated plainly and this UI
 * never claims the history is complete.
 */
function History({ customerId }: { customerId: string }) {
  const t = useT();
  const { noun } = useCustomerNoun();
  const icu = useIcu();
  const [entries, setEntries] = useState<ServiceRecordEntry[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<{ message: string; denied: boolean } | null>(null);
  const [nonce, setNonce] = useState(0);
  const gen = useRef(0);

  const load = useCallback(async (to?: string) => {
    gen.current += 1;
    const mine = gen.current;
    const inScope = scopeProbe();
    setLoading(true);
    try {
      const res = await getCustomerServiceRecord(customerId, { to, limit: SERVICE_RECORD_LIMIT_MAX });
      if (mine !== gen.current || !inScope()) return;
      setEntries((prev) => {
        const seen = new Set(prev.map((e) => e.entryId));
        return to ? [...prev, ...res.data.filter((e) => !seen.has(e.entryId))] : res.data;
      });
      setTruncated(!!res.meta?.truncated);
      setFailure(null);
    } catch (e) {
      if (mine !== gen.current || !inScope()) return;
      const status = (e as { status?: number })?.status ?? 0;
      const message = (e as { message?: string })?.message?.trim() || t("customers.detail.history.failed", "This history couldn\u2019t be read.");
      setFailure({ message, denied: status === 403 || status === 401 });
    } finally { if (mine === gen.current) setLoading(false); }
  }, [customerId, t]);

  useEffect(() => { setEntries([]); setTruncated(false); void load(); }, [customerId, nonce, load]);

  const grouped = useMemo(() => {
    const by: Record<ServiceRecordDepartment, ServiceRecordEntry[]> = { appointments: [], events: [], queues: [] };
    for (const e of entries) if (by[e.department]) by[e.department].push(e);
    for (const d of DEPARTMENTS) by[d].sort((a, b) => (a.occurredAt < b.occurredAt ? 1 : -1));
    return by;
  }, [entries]);

  // A denied history is HIDDEN, not emptied.
  if (failure?.denied) return null;

  const titles: Record<ServiceRecordDepartment, string> = {
    appointments: t("customers.detail.appointments.title", "Appointments"),
    events: t("customers.detail.events.title", "Events & registrations"),
    queues: t("customers.detail.queues.title", "Walk-in queue visits"),
  };
  const empties: Record<ServiceRecordDepartment, string> = {
    appointments: icu("customers.detail.appointments.empty", "No appointments recorded for this {noun} yet.", { noun: noun.toLowerCase() }),
    events: icu("customers.detail.events.empty", "No event registrations recorded for this {noun} yet.", { noun: noun.toLowerCase() }),
    queues: icu("customers.detail.queues.empty", "No queue visits recorded for this {noun} yet.", { noun: noun.toLowerCase() }),
  };

  return (
    <>
      {/* What this person has actually done, counted from the activity in hand.
          These are NOT directory totals: when the history is truncated the read
          holds only the most recent page, and the strip says so rather than
          presenting a partial count as the whole record. */}
      {failure ? null : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            <Kpi label={titles.appointments} value={loading && !entries.length ? EM_DASH : grouped.appointments.length} />
            <Kpi label={titles.events} value={loading && !entries.length ? EM_DASH : grouped.events.length} />
            <Kpi label={titles.queues} value={loading && !entries.length ? EM_DASH : grouped.queues.length} />
            <Kpi
              label={t("customers.detail.timeline.lastActivity", "Last activity")}
              value={entries.length ? friendlyDateTime(entries.reduce((a, e) => (e.occurredAt > a ? e.occurredAt : a), entries[0].occurredAt)) : EM_DASH}
            />
          </div>
          {truncated ? (
            <span style={{ fontSize: 11.5, color: T.soft }}>
              {t("customers.detail.timeline.countsPartial", "Counted from the activity loaded so far \u2014 earlier activity is not included yet.")}
            </span>
          ) : null}
        </div>
      )}

      {DEPARTMENTS.map((department) => (
        <DetailSection key={department} title={titles[department]}>
          {failure ? (
            <ReadError message={failure.message} onRetry={() => setNonce((n) => n + 1)} retryLabel={t("common.actions.retry", "Retry")} />
          ) : loading && !entries.length ? (
            <p style={{ margin: 0, padding: "14px 16px", fontSize: 12.5, color: T.soft }}>{t("customers.detail.history.loading", "Loading activity\u2026")}</p>
          ) : grouped[department].length === 0 ? (
            <p style={{ margin: 0, padding: "14px 16px", fontSize: 12.5, color: T.soft, fontStyle: "italic" }}>{empties[department]}</p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {grouped[department].map((entry, i) => (
                <li key={entry.entryId} style={{ padding: "11px 16px", borderTop: i ? `1px solid ${T.n6}` : 0, display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                  <span style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>
                      {entry.title || entry.containerTitle || <span style={{ color: T.soft }}>{EM_DASH}</span>}
                    </span>
                    <LifecycleChip state={entry.lifecycleState} />
                  </span>
                  <span style={{ fontSize: 11.5, color: T.soft }}>
                    {[
                      // An unscheduled walk-in still has an occurrence time; a
                      // planned window is shown ONLY when the entry carries one.
                      entry.plannedStartAt
                        ? `${friendlyDateTime(entry.plannedStartAt, entry.timezone ?? undefined)}${entry.plannedEndAt ? ` \u2014 ${friendlyTimeRange(entry.plannedStartAt, entry.plannedEndAt, entry.timezone ?? undefined).split(" \u2014 ")[1]}` : ""}`
                        : friendlyDateTime(entry.occurredAt, entry.timezone ?? undefined),
                      entry.staffName,
                      entry.locationName,
                      entry.containerTitle && entry.title ? entry.containerTitle : null,
                    ].filter(Boolean).join(" \u00b7 ")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </DetailSection>
      ))}

      {truncated && !failure ? (
        <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 14px", borderRadius: 8, background: T.white, border: `1px solid ${T.border}`, flexWrap: "wrap" }}>
          <span style={{ flex: 1, minWidth: 200, fontSize: 12.5, color: T.n2 }}>
            {t("customers.detail.timeline.truncated", "Showing the most recent activity only.")}
          </span>
          <Btn
            variant="secondary"
            size="sm"
            disabled={loading}
            onClick={() => { const bound = continuationBound(entries); if (bound) void load(bound); }}
          >{t("customers.detail.timeline.showMore", "Show earlier activity")}</Btn>
        </div>
      ) : null}
    </>
  );
}
